import { z } from 'zod';
import { leadRefSchema, refValuePattern, sanitizeRefValue } from '../../shared/schemas';
import type { LeadRef } from '../../shared/schemas';

/**
 * First-touch attribution of this tab: the UTM markers of the first URL with `utm_source` the visitor
 * opened (e.g. after a tracked link `/go/<code>`). Kept in sessionStorage, not in a cookie: it names a
 * post, not a person, so it works regardless of cookie consent and disappears with the tab.
 */
const storageKey = 'iz_ref';
const refValue = z.string().regex(refValuePattern);
const firstTouchSchema = leadRefSchema.extend({ medium: refValue.optional(), campaign: refValue.optional() });
export type FirstTouch = z.infer<typeof firstTouchSchema>;

/** Stores the current URL's UTM markers unless the tab already has a first touch. Never throws. */
export function captureFirstTouch(): void {
  try {
    if (window.sessionStorage.getItem(storageKey)) return;
    const params = new URLSearchParams(window.location.search);
    const source = sanitizeRefValue(params.get('utm_source'));
    if (!source) return;
    const touch: FirstTouch = { source };
    for (const key of ['medium', 'campaign', 'content'] as const) {
      const value = sanitizeRefValue(params.get(`utm_${key}`));
      if (value) touch[key] = value;
    }
    window.sessionStorage.setItem(storageKey, JSON.stringify(touch));
  } catch { /* storage disabled or full */ }
}

export function firstTouch(): FirstTouch | null {
  captureFirstTouch();
  try {
    const parsed = firstTouchSchema.safeParse(JSON.parse(window.sessionStorage.getItem(storageKey) ?? 'null'));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Optional `ref` of the public forms (lead, partner request, giveaway entry). */
export function firstTouchRef(): LeadRef | undefined {
  const touch = firstTouch();
  return touch ? { source: touch.source, ...(touch.content ? { content: touch.content } : {}) } : undefined;
}
